<template>
  <div
    class="mechanic-hours"
    :class="{ 'mechanic-hours-page--fullscreen p-4': fullscreen }"
  >
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title mechanic-hours__title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.SERVICE.MECHANIC_HOURS") }}
            <i class="mr-2"></i>
            <small>{{ selectedMonthLabel }}</small>
          </h3>
        </div>
        <div class="card-toolbar mechanic-hours-toolbar">
          <div class="mechanic-hours__period-controls">
            <v-btn
              small
              class="mechanic-hours__navigation-button"
              aria-label="Предишен месец"
              @click="goToPreviousMonth"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-select
              v-model="selectedMonthNumber"
              :items="monthOptions"
              item-text="label"
              item-value="value"
              dense
              outlined
              hide-details
              class="mechanic-hours-month"
            ></v-select>
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              dense
              outlined
              hide-details
              class="mechanic-hours-year"
            ></v-select>
            <v-btn
              small
              class="mechanic-hours__navigation-button"
              aria-label="Следващ месец"
              @click="goToNextMonth"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div class="mechanic-hours__actions">
            <router-link
              v-if="!fullscreen"
              :to="{ name: 'fullscreen-mechanic-hours' }"
              target="_blank"
              class="btn btn-light-primary font-weight-bolder mechanic-hours__action-button mechanic-hours__fullscreen-link"
            >
              <i class="flaticon2-expand"></i>
              {{ $t("BUTTON.FULLSCREEN") }}
            </router-link>
            <SaveButton
              v-permission="['mechanic-hours-create', 'mechanic-hours-update']"
              class="mechanic-hours__action-button"
              :disabled="!selectedCells.length"
              @save="openSaveDialog"
            />
            <DeleteButton
              v-permission="['mechanic-hours-delete']"
              class="mechanic-hours__action-button"
              :disabled="!selectedSavedEntries.length"
              @onDelete="deleteSelected"
            />
          </div>
        </div>
      </template>

      <v-card v-show="!isPageLoading">
        <div class="mechanic-hours-meta px-6 pt-4">
          <span>{{ $t("FORM_LABELS.SELECTED") }} {{ selectedCells.length }}</span>
        </div>
        <div class="mechanic-hours-legend px-6 pt-3">
          <div
            v-for="type in hourTypeOptions"
            :key="type.id"
            class="mechanic-hours-legend-item"
          >
            <span
              class="mechanic-hours-legend-swatch"
              :style="{ backgroundColor: type.color }"
            ></span>
            <strong>{{ type.abbreviation }}</strong>
            <span>{{ type.name }}</span>
          </div>
        </div>
        <div
          ref="scheduleRoot"
          class="mechanic-hours-schedule-wrap pt-3 pb-6"
          @mouseleave="stopDrag"
          @mouseup="stopDrag"
        >
          <table class="mechanic-hours-schedule">
            <thead>
              <tr>
                <th class="mechanic-hours-worker-col">
                  {{ $t("MENU.ITEM.SERVICE.MECHANICS") }}
                </th>
                <th
                  v-for="day in days"
                  :key="day.date"
                  class="mechanic-hours-day"
                  :class="{ 'is-weekend': day.isWeekend, 'is-today': day.isToday }"
                  @click="toggleColumn(day.date)"
                >
                  <span>{{ day.day }}</span>
                  <small>{{ day.weekday }}</small>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mechanic in mechanics" :key="mechanic.id">
                <th class="mechanic-hours-worker-col" @click="toggleRow(mechanic.id)">
                  {{ formatMechanicName(mechanic) }}
                </th>
                <td
                  v-for="day in days"
                  :key="mechanic.id + '-' + day.date"
                  class="mechanic-hours-cell"
                  :class="{
                    selected: isCellSelected(mechanic.id, day.date),
                    filled: !!getEntry(mechanic.id, day.date),
                    'is-weekend': day.isWeekend
                  }"
                  :style="entryStyle(getEntry(mechanic.id, day.date))"
                  @mousedown.prevent="startDrag(mechanic.id, day.date)"
                  @mouseenter="continueDrag(mechanic.id, day.date)"
                >
                  <template v-if="getEntry(mechanic.id, day.date)">
                    <strong>{{ entryTypeAbbreviation(getEntry(mechanic.id, day.date)) }}</strong>
                  </template>
                </td>
              </tr>
              <tr v-if="!mechanics.length">
                <td :colspan="days.length + 1" class="mechanic-hours-empty">
                  {{ $t("LABELS.NO_DATA") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>

    <v-dialog v-model="showSaveDialog" max-width="460px">
      <v-card>
        <v-card-title>
          {{ $t("MENU.ITEM.SERVICE.MECHANIC_HOURS") }}
        </v-card-title>
        <v-card-text>
          <v-alert v-if="formError" dense text type="error">{{ formError }}</v-alert>
          <v-select
            v-model="form.hourTypeId"
            :items="hourTypeOptions"
            item-text="name"
            item-value="id"
            :label="$t('FORM_LABELS.TYPE')"
            outlined
            dense
          >
            <template v-slot:item="{ item }">
              <span
                class="mechanic-hours-type-dot"
                :style="{ backgroundColor: item.color }"
              ></span>
              {{ item.name }}
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showSaveDialog = false">
            {{ $t("BUTTON.CANCEL") }}
          </v-btn>
          <v-btn color="primary" :loading="isLoadingMechanicHours" @click="saveSelected">
            {{ $t("BUTTON.SAVE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { mapGetters } from "vuex";
import { FETCH_MECHANICS } from "@/modules/user/store/user.module";
import SaveButton from "@/view/content/forms/components/SaveButton.vue";
import {
  DELETE_BULK_MECHANIC_HOURS,
  FETCH_MECHANIC_HOUR_TYPES,
  FETCH_MECHANIC_HOURS,
  STORE_BULK_MECHANIC_HOURS
} from "@/modules/mechanic-hours/store/mechanic-hours.module";

export default {
  components: {
    SaveButton
  },
  props: {
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const now = new Date();

    return {
      isPageLoading: true,
      mechanics: [],
      hourTypeOptions: [],
      entriesByCell: {},
      selectedCellsByKey: {},
      isDragging: false,
      dragStartCell: null,
      dragState: null,
      dragBaseCellsByKey: {},
      selectedMonthNumber: now.getMonth(),
      selectedYear: now.getFullYear(),
      showSaveDialog: false,
      form: {
        hourTypeId: null
      },
      formError: ""
    };
  },
  computed: {
    ...mapGetters(["isLoadingMechanicHours"]),
    selectedMonthKey() {
      return `${this.selectedYear}-${String(this.selectedMonthNumber + 1).padStart(2, "0")}`;
    },
    selectedMonthLabel() {
      return `${String(this.selectedMonthNumber + 1).padStart(2, "0")}.${this.selectedYear}`;
    },
    monthOptions() {
      return Array.from({ length: 12 }, (_, month) => {
        const label = new Date(this.selectedYear, month, 1).toLocaleDateString("bg-BG", {
          month: "long"
        });

        return {
          value: month,
          label: label.charAt(0).toUpperCase() + label.slice(1)
        };
      });
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 5;

      return Array.from({ length: 11 }, (_, index) => startYear + index);
    },
    days() {
      const today = new Date();
      const daysInMonth = new Date(this.selectedYear, this.selectedMonthNumber + 1, 0).getDate();

      return Array.from({ length: daysInMonth }, (_, index) => {
        const date = new Date(this.selectedYear, this.selectedMonthNumber, index + 1);
        const value = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, "0"),
          String(date.getDate()).padStart(2, "0")
        ].join("-");

        return {
          date: value,
          day: index + 1,
          weekday: date.toLocaleDateString("bg-BG", { weekday: "short" }),
          isWeekend: [0, 6].includes(date.getDay()),
          isToday:
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate()
        };
      });
    },
    selectedCells() {
      return Object.values(this.selectedCellsByKey);
    },
    selectedSavedEntries() {
      return this.selectedCells
        .map(cell => this.getEntry(cell.userId, cell.date))
        .filter(entry => !!entry);
    }
  },
  watch: {
    selectedMonthKey() {
      this.selectedCellsByKey = {};
      this.fetchHours();
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.MECHANIC_HOURS"),
        route: { name: "mechanic-hours" }
      },
      { title: this.$i18n.t("LABELS.LIST") }
    ]);

    this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      try {
        await Promise.all([this.fetchMechanics(), this.fetchHourTypes(), this.fetchHours()]);
      } finally {
        this.isPageLoading = false;
      }
    },
    async fetchMechanics() {
      const data = await this.$store.dispatch(FETCH_MECHANICS, "?length=1000&start=0");
      this.mechanics = data.data.items || [];
    },
    async fetchHourTypes() {
      const data = await this.$store.dispatch(FETCH_MECHANIC_HOUR_TYPES);
      this.hourTypeOptions = data.data || [];
      this.form.hourTypeId = this.hourTypeOptions[0] ? this.hourTypeOptions[0].id : null;
    },
    async fetchHours() {
      const data = await this.$store.dispatch(
        FETCH_MECHANIC_HOURS,
        `?month=${this.selectedMonthKey}`
      );
      const entries = Array.isArray(data.data) ? data.data : [];

      this.entriesByCell = entries.reduce((items, entry) => {
        items[this.cellKey(entry.userId, entry.date)] = entry;
        return items;
      }, {});
    },
    cellKey(userId, date) {
      return `${userId}-${date}`;
    },
    getEntry(userId, date) {
      return this.entriesByCell[this.cellKey(userId, date)];
    },
    isCellSelected(userId, date) {
      return !!this.selectedCellsByKey[this.cellKey(userId, date)];
    },
    setCellSelected(userId, date, selected) {
      const key = this.cellKey(userId, date);
      const cells = { ...this.selectedCellsByKey };

      if (selected) {
        cells[key] = { userId, date };
      } else {
        delete cells[key];
      }

      this.selectedCellsByKey = cells;
    },
    startDrag(userId, date) {
      const selected = !this.isCellSelected(userId, date);

      this.isDragging = true;
      this.dragStartCell = { userId, date };
      this.dragState = selected;
      this.dragBaseCellsByKey = { ...this.selectedCellsByKey };
      this.applyDragRectangle(userId, date);
    },
    continueDrag(userId, date) {
      if (!this.isDragging || this.dragState === null) {
        return;
      }

      this.applyDragRectangle(userId, date);
    },
    stopDrag() {
      this.isDragging = false;
      this.dragStartCell = null;
      this.dragState = null;
      this.dragBaseCellsByKey = {};
    },
    applyDragRectangle(userId, date) {
      if (!this.dragStartCell || this.dragState === null) {
        return;
      }

      const cells = { ...this.dragBaseCellsByKey };

      this.getRectangleCells(this.dragStartCell, { userId, date }).forEach(cell => {
        const key = this.cellKey(cell.userId, cell.date);

        if (this.dragState) {
          cells[key] = cell;
        } else {
          delete cells[key];
        }
      });

      this.selectedCellsByKey = cells;
    },
    getRectangleCells(startCell, endCell) {
      const startMechanicIndex = this.mechanics.findIndex(mechanic => mechanic.id === startCell.userId);
      const endMechanicIndex = this.mechanics.findIndex(mechanic => mechanic.id === endCell.userId);
      const startDayIndex = this.days.findIndex(day => day.date === startCell.date);
      const endDayIndex = this.days.findIndex(day => day.date === endCell.date);

      if ([startMechanicIndex, endMechanicIndex, startDayIndex, endDayIndex].some(index => index < 0)) {
        return [];
      }

      const mechanicStart = Math.min(startMechanicIndex, endMechanicIndex);
      const mechanicEnd = Math.max(startMechanicIndex, endMechanicIndex);
      const dayStart = Math.min(startDayIndex, endDayIndex);
      const dayEnd = Math.max(startDayIndex, endDayIndex);
      const cells = [];

      this.mechanics.slice(mechanicStart, mechanicEnd + 1).forEach(mechanic => {
        this.days.slice(dayStart, dayEnd + 1).forEach(day => {
          cells.push({ userId: mechanic.id, date: day.date });
        });
      });

      return cells;
    },
    toggleRow(userId) {
      const shouldSelect = this.days.some(day => !this.isCellSelected(userId, day.date));

      this.days.forEach(day => this.setCellSelected(userId, day.date, shouldSelect));
    },
    toggleColumn(date) {
      const shouldSelect = this.mechanics.some(mechanic => !this.isCellSelected(mechanic.id, date));

      this.mechanics.forEach(mechanic => this.setCellSelected(mechanic.id, date, shouldSelect));
    },
    openSaveDialog() {
      if (!this.selectedCells.length) {
        return;
      }

      const firstEntry = this.getEntry(this.selectedCells[0].userId, this.selectedCells[0].date);
      this.form.hourTypeId = firstEntry
        ? firstEntry.mechanicHourTypeId
        : this.hourTypeOptions[0] && this.hourTypeOptions[0].id;
      this.formError = "";
      this.showSaveDialog = true;
    },
    async saveSelected() {
      if (!this.form.hourTypeId) {
        this.formError = this.$t("VALIDATION.INVALID", { name: this.$t("FORM_LABELS.TYPE") });
        return;
      }

      try {
        await this.$store.dispatch(STORE_BULK_MECHANIC_HOURS, {
          hourTypeId: this.form.hourTypeId,
          entries: this.selectedCells
        });
        this.showSaveDialog = false;
        this.selectedCellsByKey = {};
        await this.fetchHours();
        this.notifySuccess(this.$t("MECHANIC_HOURS.SAVED_SUCCESS"));
      } catch (response) {
        this.formError = response && response.data && response.data.message
          ? response.data.message
          : this.$t("LABELS.SAVE_ERROR");
      }
    },
    async deleteSelected() {
      if (!this.selectedSavedEntries.length) {
        return;
      }

      await this.$store.dispatch(DELETE_BULK_MECHANIC_HOURS, {
        ids: this.selectedSavedEntries.map(entry => entry.id)
      });
      this.selectedCellsByKey = {};
      await this.fetchHours();
      this.notifySuccess(this.$t("MECHANIC_HOURS.DELETED_SUCCESS"));
    },
    goToPreviousMonth() {
      const date = new Date(this.selectedYear, this.selectedMonthNumber - 1, 1);
      this.selectedYear = date.getFullYear();
      this.selectedMonthNumber = date.getMonth();
    },
    goToNextMonth() {
      const date = new Date(this.selectedYear, this.selectedMonthNumber + 1, 1);
      this.selectedYear = date.getFullYear();
      this.selectedMonthNumber = date.getMonth();
    },
    formatMechanicName(mechanic) {
      return `${mechanic.firstName || ""} ${mechanic.lastName || ""}`.trim() || mechanic.email;
    },
    getEntryType(entry) {
      if (!entry) {
        return null;
      }

      if (entry.mechanicHourType && entry.mechanicHourType.id) {
        return entry.mechanicHourType;
      }

      return this.hourTypeOptions.find(type => type.id === entry.mechanicHourTypeId) || null;
    },
    entryTypeAbbreviation(entry) {
      const type = this.getEntryType(entry);

      return type ? type.abbreviation : "";
    },
    entryStyle(entry) {
      const type = this.getEntryType(entry);

      if (!type || !type.color) {
        return {};
      }

      return {
        "--mechanic-hour-color": type.color
      };
    },
    notifySuccess(text) {
      this.$notify({
        group: "notify",
        type: "success",
        text
      });
    }
  }
};
</script>

<style scoped>
.mechanic-hours-toolbar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.mechanic-hours__period-controls,
.mechanic-hours__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.mechanic-hours__navigation-button {
  background-color: #e1f0ff !important;
  color: #3699ff !important;
  min-width: 2.5rem !important;
}

.mechanic-hours-page--fullscreen {
  min-width: 0;
  width: 100%;
}

.mechanic-hours-page--fullscreen ::v-deep .card,
.mechanic-hours-page--fullscreen ::v-deep .v-card {
  min-width: 0;
  width: 100%;
}

.mechanic-hours-page--fullscreen ::v-deep .card-body {
  min-width: 0;
  width: 100%;
}

.mechanic-hours-month {
  max-width: 150px;
}

.mechanic-hours-year {
  max-width: 104px;
}

.mechanic-hours-schedule-wrap {
  box-sizing: border-box;
  margin-left: 1.5rem;
  margin-right: 2.25rem;
  max-width: calc(100% - 3.75rem);
  overflow: auto;
  width: 100%;
}

.mechanic-hours-schedule {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
}

@media (min-width: 768px) {
  .mechanic-hours-schedule {
    min-width: max-content;
    width: 100%;
  }
}

.mechanic-hours-schedule th,
.mechanic-hours-schedule td {
  border-bottom: 2px solid #cbd5e1;
  border-right: 2px solid #cbd5e1;
}

.mechanic-hours-schedule thead th {
  border-top: 2px solid #cbd5e1;
}

.mechanic-hours-worker-col {
  background: #ffffff;
  border-left: 2px solid #cbd5e1;
  cursor: pointer;
  left: 0;
  min-width: 180px;
  padding: 7px 10px;
  position: sticky;
  text-align: left;
  z-index: 2;
}

thead .mechanic-hours-worker-col {
  top: 0;
  z-index: 4;
}

.mechanic-hours-day {
  background: #ffffff;
  cursor: pointer;
  min-width: 40px;
  padding: 5px 2px;
  position: sticky;
  text-align: center;
  top: 0;
  z-index: 3;
}

.mechanic-hours-day span,
.mechanic-hours-day small {
  display: block;
}

.mechanic-hours-day small {
  color: #6b7280;
  font-size: 10px;
}

.mechanic-hours-cell {
  background: #ffffff;
  cursor: pointer;
  height: 34px;
  min-width: 40px;
  text-align: center;
  user-select: none;
}

.mechanic-hours-cell strong {
  display: block;
  line-height: 13px;
}

.mechanic-hours-cell.is-weekend,
.mechanic-hours-day.is-weekend {
  background-color: #eef2f7;
}

.mechanic-hours-cell.is-weekend {
  box-shadow: none;
}

.mechanic-hours-day.is-weekend {
  color: #334155;
  font-weight: 700;
}

.mechanic-hours-day.is-today {
  box-shadow: inset 0 3px 0 #3699ff;
}

.mechanic-hours-cell.selected {
  outline: 2px solid #3699ff;
  outline-offset: -2px;
}

.mechanic-hours-cell.filled {
  background-color: var(--mechanic-hour-color, #ffffff) !important;
  box-shadow: inset 4px 0 0 var(--mechanic-hour-color, #ffffff);
  color: #1f2937;
}

.mechanic-hours-cell.is-weekend.filled {
  box-shadow: inset 0 0 0 9999px rgba(255, 255, 255, 0.18);
}

.mechanic-hours-empty {
  padding: 28px;
  text-align: center;
}

.mechanic-hours-meta {
  color: #6b7280;
  font-size: 13px;
}

.mechanic-hours-legend {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mechanic-hours-legend-item {
  align-items: center;
  border: 1px solid #d1d5db;
  display: inline-flex;
  font-size: 12px;
  gap: 6px;
  padding: 4px 8px;
}

.mechanic-hours-legend-swatch {
  border: 1px solid #9ca3af;
  display: inline-block;
  height: 14px;
  width: 14px;
}

.mechanic-hours-type-dot {
  border: 1px solid #d1d5db;
  border-radius: 50%;
  display: inline-block;
  height: 14px;
  margin-right: 8px;
  width: 14px;
}

@media (max-width: 767.98px) {
  .mechanic-hours ::v-deep .card-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .mechanic-hours__title {
    flex: 1 1 100%;
    margin-bottom: 0.75rem;
  }

  .mechanic-hours__title .card-label small {
    display: none;
  }

  .mechanic-hours-toolbar,
  .mechanic-hours__period-controls,
  .mechanic-hours__actions {
    flex: 1 1 100%;
  }

  .mechanic-hours-toolbar {
    gap: 0.75rem;
  }

  .mechanic-hours__period-controls {
    display: grid;
    gap: 0.5rem;
    grid-template-columns: 2.5rem minmax(0, 1fr) minmax(5.5rem, 0.55fr) 2.5rem;
  }

  .mechanic-hours-month,
  .mechanic-hours-year {
    max-width: none;
    min-width: 0;
    width: 100%;
  }

  .mechanic-hours__actions {
    display: grid;
    gap: 0.5rem;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
  }

  .mechanic-hours__action-button {
    margin-right: 0 !important;
  }

  .mechanic-hours__fullscreen-link {
    display: none;
  }

  .mechanic-hours-worker-col {
    max-width: 120px;
    min-width: 120px;
    position: static;
  }
}

@media (max-width: 575.98px) {
  .mechanic-hours__period-controls {
    grid-template-columns: minmax(0, 1fr) minmax(6.5rem, 0.65fr);
  }

  .mechanic-hours__navigation-button {
    display: none;
  }
}

</style>
